/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" */
import isPropValid from '@emotion/is-prop-valid';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import { animated, useSpring } from '@react-spring/web';
import React, {
    Fragment,
    FunctionComponent,
    ReactNode,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import Arrow from '@toucan/client/global/icons/legacy/small-arrow.svg';
import Tick from '@toucan/client/global/icons/legacy/tick-filled.svg';

import { useKeyPress } from '@toucan/client/shared/hooks/use-key-press';
import { useMeasure } from '@toucan/client/shared/hooks/use-measure';

import { styles } from './accordion.styles';

export enum AccordionVariant {
    INTERACTIVE = 'interactive',
    STATIC = 'static',
}

export enum AccordionLayout {
    CHECKOUT = 'checkout',
    ARROW = 'arrow',
}

type DynamicTitle = (arg: number) => ReactNode;

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface AccordionSection {
    /**
     * The title of the accordion
     */
    title: ReactNode | DynamicTitle;

    /**
     * A summary to show under the title.
     * Only works if using the CHECKOUT layout
     */
    summary?: ReactNode | string;

    /**
     * If a tick box should be shown.
     * Only works if using the CHECKOUT layout
     *
     * @default false
     */
    showTickBox?: boolean;

    /**
     * The id for the sections. Used for selecting activeSection & accessibility
     * NOTE: Each section should have unique ids
     */
    id: string;

    /**
     * The React child to show for the content
     */
    content: ReactNode;

    /**
     * If the section is disabled and can't be edited
     *
     * @default false
     */
    disabled?: boolean;

    /**
     * If node should be shown
     */
    showTitleChildNode?: boolean;

    /**
     * An extra node that will be placed in the heading.
     * The location depends on the layout of the Accordion
     */
    titleChildNode?: ReactNode;

    /**
     * An extra node that will be placed in the heading.
     * But only when the section is open
     */
    titleActionNode?: ReactNode;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface AccordionProps {
    /**
     * The variant of the accordion
     *
     * 1. INTERACTIVE
     * 2. STATIC
     *
     * @default INTERACTIVE
     */
    variant?: AccordionVariant;

    /**
     * The layout of the accordion
     * 1. CHECKOUT
     * 2. ARROW
     *
     * @default ARROW
     */
    layout?: AccordionLayout;

    /**
     * The id of the active section
     *
     * null for when there are no active section
     */
    activeSection: string | null;

    /**
     * The sections to the accordion
     */
    sections: AccordionSection[];

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * A function that will be called when the active section changes
     * NOTE: Isn't needed if using STATIC variant
     */
    onActiveSectionChange?: (
        activeSection: string | null
    ) => Promise<void> | void;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface ExpandableProps {
    /**
     * If the component is open
     */
    isOpen: boolean;

    /**
     * The id used for the accordion section
     */
    id: string;

    /**
     * The content to place in the component
     */
    content: ReactNode;
}

type AccordionSectionProps = Pick<
    AccordionProps,
    'variant' | 'activeSection' | 'layout' | 'onActiveSectionChange'
> & {
    /**
     * The section of the accordion to render
     */
    section: AccordionSection;

    /**
     * Index of the accordion item
     */
    index: number;
};

const StyledAccordion = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

/**
 * A component that expands or collapses to show content
 * https://codesandbox.io/embed/lp80n9z7v9
 */
const Expandable = ({ isOpen, id, content }: ExpandableProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const contentID = `${id}-content`;
    const buttonID = `${id}-button`;

    const { height } = useMeasure(contentRef);

    const [isAlreadyOpen, setAlreadyOpenState] = useState(false);

    // 1px when closed to show the border on the panel
    const props = useSpring({
        from: { height: 1 },
        to: {
            height: isOpen ? height : 1,
        },
        immediate: isAlreadyOpen,
        onStart() {
            if (isOpen && contentRef.current) {
                contentRef.current.style.visibility = 'visible';
            }

            if (panelRef.current) {
                panelRef.current.style.overflow = 'hidden';
            }
        },
        onRest() {
            if (!isOpen && contentRef.current) {
                setAlreadyOpenState(false);
                contentRef.current.style.visibility = 'hidden';
            }

            if (isOpen && panelRef.current) {
                setAlreadyOpenState(true);
                panelRef.current.style.overflow = 'visible';
            }
        },
    });

    return (
        <animated.div
            style={props}
            id={contentID}
            role="region"
            aria-labelledby={buttonID}
            aria-hidden={!isOpen}
            className="panel"
            ref={panelRef}
        >
            <div ref={contentRef} className="panel-inner">
                {/* Extra element so padding is included in height of panel-inner */}
                {isOpen && <div className="panel-content">{content}</div>}
            </div>
        </animated.div>
    );
};

/**
 * A component that handles each accordion section
 */
const AccordionSection = ({
    section,
    layout,
    variant,
    activeSection,
    onActiveSectionChange,
    index,
}: AccordionSectionProps) => {
    const {
        id,
        title,
        content,
        summary,
        showTitleChildNode,
        titleChildNode,
        titleActionNode,
        disabled = false,
        showTickBox = false,
    } = section;

    const isSectionOpen =
        variant === AccordionVariant.STATIC ? true : activeSection === id;

    const ariaDisabled = variant === AccordionVariant.STATIC ? true : disabled;

    const buttonID = `${id}-button`;
    const contentID = `${id}-content`;

    const showExtra = !isSectionOpen && (summary || showTitleChildNode);

    const showActionNode = isSectionOpen && titleActionNode;

    let buttonClassName = 'title-button';
    if (showExtra) {
        buttonClassName += ' title-button--less-padding';
    }

    const buttonProps = {
        'aria-expanded': isSectionOpen,
        'aria-controls': contentID,
        id: buttonID,
        'aria-disabled': ariaDisabled,
        disabled: ariaDisabled,
        className: buttonClassName,
        'data-accordion-index': index,
        'data-accordion-button': true,
        onClick: () => {
            // If already active section then close section when clicked again
            const newActiveSection = id === activeSection ? null : id;
            onActiveSectionChange && onActiveSectionChange(newActiveSection);
        },
    };

    const headingProps = {
        className: 'title',
        size: HeadingSize.H3,
        'data-testid': section['data-testid'],
    };

    const expandable = (
        <Expandable id={id} content={content} isOpen={isSectionOpen} />
    );

    function view(layout: AccordionSectionProps['layout']) {
        switch (layout) {
            case AccordionLayout.CHECKOUT:
                return (
                    <Fragment>
                        <Heading {...headingProps}>
                            <button {...buttonProps}>
                                <div className="title-text">
                                    {typeof title === 'function'
                                        ? title(index + 1)
                                        : title}
                                </div>
                            </button>
                            {showActionNode && (
                                <div className="title-action">
                                    {titleActionNode}
                                </div>
                            )}
                            {showTickBox && (
                                <div className="tick-wrapper">
                                    <div className="tick">
                                        <Tick data-testid="accordion__section__check" />
                                    </div>
                                </div>
                            )}
                            {showExtra && (
                                <Fragment>
                                    {summary && (
                                        <div className="title-extra title-summary">
                                            {summary}
                                        </div>
                                    )}
                                    {showTitleChildNode && (
                                        <div className="title-extra-node-wrapper">
                                            <div className="title-extra title-extra-node">
                                                {titleChildNode}
                                            </div>
                                        </div>
                                    )}
                                </Fragment>
                            )}
                        </Heading>
                        {expandable}
                    </Fragment>
                );

            case AccordionLayout.ARROW:
            default:
                return (
                    <Fragment>
                        <Heading {...headingProps}>
                            <button {...buttonProps}>
                                <Arrow className="title-arrow" />
                                <div className="title-text">
                                    {typeof title === 'function'
                                        ? title(index + 1)
                                        : title}
                                </div>
                            </button>
                            {showTitleChildNode && (
                                <div className="title-extra-node">
                                    {titleChildNode}
                                </div>
                            )}
                        </Heading>
                        {expandable}
                    </Fragment>
                );
        }
    }

    return view(layout);
};

export const Accordion: FunctionComponent<AccordionProps> = ({
    sections,
    activeSection,
    onActiveSectionChange,
    layout = AccordionLayout.ARROW,
    variant = AccordionVariant.INTERACTIVE,
    ...otherProps
}: AccordionProps) => {
    const accordionRef = useRef<HTMLDivElement>(null);
    const windowRef = useRef<Window>(
        typeof window !== 'undefined' ? window : null
    );

    /**
     * Added handler to window instead of StyledAccordion as otherwise it wouldn't
     * fire the on keydown if the buttons hadn't been clicked (just been tabbed to)
     */
    useKeyPress(
        windowRef.current,
        ['ArrowUp', 'Up', 'ArrowDown', 'Down', 'Home', 'End'],
        (event: KeyboardEvent) => {
            if (
                accordionRef.current === null ||
                !document.activeElement ||
                !document.activeElement.matches(`[data-accordion-button]`) ||
                !accordionRef.current.contains(document.activeElement) // checks that button belongs to this accordion
            ) {
                return;
            }
            const indexString = document.activeElement.getAttribute(
                'data-accordion-index'
            );
            if (indexString === null) return;

            const index = parseInt(indexString);

            /**
             * Gets the index of item to be selected next
             *
             * @param index The currently selected index
             * @param code The code of the key pressed
             * @returns {number} The new index
             */
            function getNewIndex(index: number, code: string) {
                /**
                 * Gets a modifier value used when the item that should be selected is disabled
                 *
                 * @returns {number} The modifier to use for finding the next item
                 */
                function getDisabledModifier() {
                    switch (code) {
                        case 'ArrowUp':
                        case 'Up':
                        case 'End':
                            return -1;

                        case 'ArrowDown':
                        case 'Down':
                        case 'Home':
                            return 1;

                        default:
                            throw new Error('Key not supported');
                    }
                }

                /**
                 * Gets the index of what should be the next selected item
                 *
                 * @returns {number} The new index
                 */
                function getIndex() {
                    switch (code) {
                        case 'ArrowUp':
                        case 'Up':
                            return (
                                (index - 1 + sections.length) % sections.length
                            );

                        case 'ArrowDown':
                        case 'Down':
                            return (index + 1) % sections.length;

                        case 'Home':
                            return 0;

                        case 'End':
                            return sections.length - 1;

                        default:
                            return index;
                    }
                }

                /**
                 * Checks if the accordion item is disabled
                 *
                 * @param index The index to check
                 * @returns {boolean} If the item is disabled
                 */
                function isAccordionItemDisabled(index: number) {
                    return sections[index].disabled;
                }

                let newIndex = getIndex();
                // Finds the next selectable accordion item
                while (
                    isAccordionItemDisabled(newIndex) &&
                    newIndex !== index
                ) {
                    const modifier = getDisabledModifier();
                    newIndex += modifier;
                }

                return newIndex;
            }

            const newIndex = getNewIndex(index, event.code);

            const buttonToSelect = accordionRef.current.querySelector(
                `[data-accordion-index="${newIndex}"]`
            );

            if (buttonToSelect === null) return;
            (buttonToSelect as HTMLButtonElement).focus();
        }
    );

    return (
        <StyledAccordion
            activeSection={activeSection}
            sections={sections}
            ref={accordionRef}
            layout={layout}
            {...otherProps}
        >
            {sections.map((section, i) => (
                <AccordionSection
                    key={section.id}
                    section={section}
                    variant={variant}
                    layout={layout}
                    index={i}
                    activeSection={activeSection}
                    onActiveSectionChange={onActiveSectionChange}
                />
            ))}
        </StyledAccordion>
    );
};
